import { useRef } from "react";
import { renderDisplayText, cn } from "@/lib/utils";
import { useParallax } from "@/hooks/useTenantMotion";
import { TenantImage } from "@/components/home/TenantImage";
import { Skel } from "@/components/home/TenantSkeletons";
import resultsLivingRoom from "@/assets/results-living-room.jpg";
import resultsKitchen from "@/assets/results-kitchen.jpg";
import resultsBathroom from "@/assets/results-bathroom.jpg";

export interface ResultImage {
  url?: string;
  alt?: string;
}

const DEFAULT_RESULT_IMAGES: Required<ResultImage>[] = [
  { url: resultsLivingRoom, alt: "Spotless living room after cleaning" },
  { url: resultsKitchen, alt: "Sparkling clean kitchen" },
  { url: resultsBathroom, alt: "Fresh and clean bathroom" },
];

interface ResultsProps {
  eyebrow?: string;
  title: string;
  description?: string;
  /** Tenant-edited result images (About admin); falls back to the stock set. */
  images?: ResultImage[];
  className?: string;
  /** Tenant images still loading — show shimmering frames, never the stock set first. */
  loading?: boolean;
}

/** Photo inside a frame that drifts gently against the scroll. */
function ParallaxShot({ url, alt, className, strength }: { url?: string; alt: string; className: string; strength: number }) {
  const ref = useRef<HTMLDivElement>(null);
  useParallax(ref, strength);
  return (
    <div ref={ref} className={cn("tp-media relative", className)}>
      {url ? (
        <div className="tp-parallax">
          <TenantImage src={url} alt={alt} className="tp-zoom" loading="lazy" />
        </div>
      ) : (
        <Skel className="absolute inset-0 h-full w-full rounded-none" />
      )}
    </div>
  );
}

/** Editorial results gallery: one large image with two stacked companions. */
export function Results({ eyebrow = "Results", title, description, images, className, loading = false }: ResultsProps) {
  const shots = loading
    ? DEFAULT_RESULT_IMAGES.map((image) => ({ url: undefined as string | undefined, alt: image.alt }))
    : (images?.length ? images : DEFAULT_RESULT_IMAGES).slice(0, 3).map((image, index) => {
        const fallback = DEFAULT_RESULT_IMAGES[index % DEFAULT_RESULT_IMAGES.length];
        return { url: image.url || fallback.url, alt: image.alt || fallback.alt };
      });

  return (
    <section className={cn("tp-section", className)}>
      <div className="container mx-auto px-5 md:px-6">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="tp-eyebrow" data-reveal>{eyebrow}</p>
            <h2 className="tp-h2 mt-5" data-reveal style={{ ["--reveal-delay" as string]: "70ms" }}>
              {renderDisplayText(title)}
            </h2>
          </div>
          {description && (
            <p className="tp-lead lg:col-span-5 lg:pb-1" data-reveal style={{ ["--reveal-delay" as string]: "140ms" }}>
              {renderDisplayText(description)}
            </p>
          )}
        </div>

        <div
          className={cn(
            "tp-scroll-x -mx-5 mt-12 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 md:mx-0 md:mt-16 md:grid md:gap-6 md:overflow-visible md:px-0 md:pb-0",
            shots.length >= 3 ? "md:grid-cols-12 md:grid-rows-2" : shots.length === 2 ? "md:grid-cols-2" : "md:grid-cols-1",
          )}
        >
          {shots.map((shot, index) => {
            const lead = shots.length >= 3 && index === 0;
            return (
              <figure
                key={`${shot.url ?? "loading"}-${index}`}
                className={cn(
                  "group relative w-[82%] shrink-0 snap-center md:w-auto",
                  shots.length >= 3 && (lead ? "md:col-span-7 md:row-span-2" : "md:col-span-5"),
                )}
                data-reveal="mask"
                style={{ ["--reveal-delay" as string]: `${index * 120}ms` }}
              >
                <ParallaxShot
                  url={shot.url}
                  alt={shot.alt}
                  strength={lead ? 0.05 : 0.035}
                  className={lead ? "aspect-[4/5] md:aspect-auto md:h-full md:min-h-[34rem]" : "aspect-[4/5] md:aspect-[16/10]"}
                />
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}
